// services/conversationService.ts
import { supabase } from '@/lib/supabaseClient';
import type { ChatMessage, ChatConversation } from '../types/chat.types';

export const conversationService = {
  async createConversation(data: {
    leadId?: number;
    source: string;
  }): Promise<ChatConversation> {
    const { data: conversation, error } = await supabase
      .from('chat_conversations')
      .insert({
        lead_id: data.leadId,
        source: data.source,
        visitor_id: this.generateVisitorId()
      })
      .select()
      .single();

    if (error) throw error;
    return conversation;
  },

  async getConversation(id: string): Promise<ChatConversation | null> {
    const { data, error } = await supabase
      .from('chat_conversations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return null;
    return data;
  },

  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('timestamp', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async saveMessage(message: ChatMessage): Promise<void> {
    const { error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: message.conversationId,
        role: message.role,
        content: message.content,
        attachments: message.attachments,
        metadata: message.metadata
      });

    if (error) throw error;
  },

  async endConversation(conversationId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_conversations')
      .update({ 
        status: 'ended',
        ended_at: new Date().toISOString()
      })
      .eq('id', conversationId);

    if (error) throw error;
  },

  async getConversationContext(conversationId: string): Promise<any> {
    // Pobierz historię konwersacji
    const messages = await this.getMessages(conversationId);
    
    // Pobierz dane konwersacji
    const conversation = await this.getConversation(conversationId);
    
    // Pobierz dane leada jeśli istnieje
    let leadInfo = null;
    if (conversation?.leadId) {
      const { data: lead } = await supabase
        .from('leads')
        .select('*')
        .eq('id', conversation.leadId)
        .single();
      leadInfo = lead;
    }

    // Przygotuj wiadomości bez id dla Claude API
    const cleanMessages = messages.slice(-10).map(msg => ({
      role: msg.role,
      content: msg.content
    }));

    return {
      conversationId,
      leadInfo,
      previousMessages: cleanMessages, // Ostatnie 10 wiadomości bez id
      messageCount: messages.length
    };
  },

  generateVisitorId(): string {
    return `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};